import React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";
import { Link } from "react-router-dom";

export default function SingleVerticalLogistics() {
  return (
    <div className="single-vertical-parent">
      <div className="vertical-title">
        <div className="text-900 font-bold text-7xl mb-4 text-center">
          FlexNet Logistics: Streamlining Supply Chains with 5G Connectivity
        </div>
        <div className="text-700 text-xl mb-6 text-center line-height-3">
          Optimize inventory management, vehicle tracking, and warehouse
          operations with FlexNet's private 5G solutions for logistics.
        </div>
      </div>
      {/* Hero Section */}
      <div className="grid grid-nogutter surface-section text-800 ">
        <div className="col-12 md:col-6 p-6 text-center md:text-left flex align-items-center">
          <section>
            <span className="block text-6xl font-bold mb-3">
              FlexNet Logistics
            </span>
            <div className="text-6xl text-primary font-bold mb-3">
              Revolutionizing Operations with 5G Connectivity
            </div>
            <p className="mt-0 mb-4 text-700 line-height-3">
              FlexNet revolutionizes construction with reliable 5G connectivity
              for enhanced worker safety, real-time equipment tracking, and
              optimized project management.
            </p>
            <button className="flex underline align-items-center mt-4">
              <Link to="/connectivity">Request Demo</Link>
              <FaExternalLinkAlt className="ml-2" />
            </button>
          </section>
        </div>
        <div className="col-12 md:col-6 overflow-hidden">
          <img
            src="https://www.lightguidesys.com/wp-content/uploads/2024/06/smart-warehouse-technology.png"
            alt="hero-1"
            className="md:ml-auto block md:h-full"
            style={{ clipPath: "polygon(8% 0, 100% 0%, 100% 100%, 0 100%)" }}
          />
        </div>
      </div>

      {/* Key Benefits Section */}
      <div className="surface-section px-4 py-8 md:px-6 lg:px-8 text-center">
        <div className="mb-3 font-bold text-3xl">
          <span className="text-900">Why </span>
          <span className="text-blue-600">FlexNet for Logistics?</span>
        </div>
        <div className="grid justify-center">
          {keyBenefits.map((benefit, index) => (
            <div key={index} className="col-12 md:col-4 mb-4 px-5">
              <span
                className="p-3 shadow-2 mb-3 inline-block surface-card"
                style={{ borderRadius: "10px" }}
              >
                <i className={`${benefit.icon} text-4xl text-blue-500`}></i>
              </span>
              <div className="text-900 text-xl mb-3 font-medium">
                {benefit.title}
              </div>
              <span className="text-700 line-height-3">{benefit.detail}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Use Cases Section */}

      <div className="mb-3 font-bold text-3xl">
        <span className="text-900">FlexNet Logistics </span>
        <span className="text-blue-600">Use Cases</span>
      </div>
      {useCases.map((useCase, index) => (
        <div
          key={index}
          className="surface-section px-4 py-8 md:px-6 lg:px-8 text-center"
        >
          <div className="b-3 font-bold text-2xl mb-3">
            <span className="text-blue-600">Use Case</span>
            {useCase.title}
          </div>
          <div className="text-700 mb-6">{useCase.description}</div>
          <div className="grid">
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">Benefits</div>
              {useCase.benefits.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">
                Devices/Technologies
              </div>
              {useCase.devices.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>
            <div className="col-12 md:col-4 mb-4 px-5">
              <div className="text-900 text-xl mb-3 font-medium">
                Applications
              </div>
              {useCase.applications.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <i className={`${item.icon} text-blue-500 mr-2`}></i>
                  {item.title}
                </div>
              ))}
            </div>
            <button className="flex underline align-items-center mt-4 vertical-market-link">
              <Link to="/marketplace">Request Access to Marketplace</Link>
              <FaExternalLinkAlt className="ml-2" />
            </button>
          </div>
        </div>
      ))}

      {/* Call-to-Action Section */}
      <div className="surface-section text-center py-8">
        <div className="text-900 text-3xl font-bold mb-4">
          Ready to Optimize Your Logistics Operations?
        </div>

        <button className="p-button p-button-raised connectivity-request-demo-button p-3">
          <Link to="contact-page">Get in Touch for a Personalized Demo</Link>
        </button>
      </div>
    </div>
  );
}

const keyBenefits = [
  {
    title: "Real-Time Inventory Tracking and Management",
    detail: "Monitor stock levels with IoT and private 5G networks.",
    icon: "pi pi-box",
  },
  {
    title: "Autonomous Vehicle Operation and Optimization",
    detail: "Enhance material transport efficiency with AGVs and AMRs.",
    icon: "pi pi-car",
  },
  {
    title: "Enhanced Supply Chain Visibility and Efficiency",
    detail: "Ensure seamless logistics operations with real-time insights.",
    icon: "pi pi-globe",
  },
  {
    title: "Improved Warehouse Automation with IoT Integration",
    detail: "Increase speed and accuracy of warehouse operations.",
    icon: "pi pi-cog",
  },
  {
    title: "Secure, Low-Latency Data Transfer for Critical Operations",
    detail: "Maintain secure, real-time data flow for logistics processes.",
    icon: "pi pi-lock",
  },
];

const useCases = [
  {
    title: " 1: Real-Time Inventory Management with IoT Sensors",
    description:
      "Utilize IoT sensors connected via private 5G networks to monitor inventory levels in real-time, ensuring optimal stock management and reducing delays.",
    benefits: [
      {
        title: "Minimize stockouts and overstock situations",
        icon: "pi pi-box",
      },
      { title: "Enhance supply chain transparency", icon: "pi pi-eye" },
      { title: "Automate replenishment processes", icon: "pi pi-sync" },
    ],
    devices: [
      { title: "IoT Sensors", icon: "pi pi-cloud" },
      { title: "RFID Tags", icon: "pi pi-tag" },
      { title: "Barcode Scanners", icon: "pi pi-barcode" },
    ],
    applications: [
      { title: "Inventory Management Systems", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 2: Autonomous Vehicle Management (AGVs/AMRs)",
    description:
      "Implement 5G-connected Automated Guided Vehicles (AGVs) and Autonomous Mobile Robots (AMRs) for efficient material handling within warehouses and logistics centers.",
    benefits: [
      {
        title: "Optimize route planning and reduce manual labor",
        icon: "pi pi-map",
      },
      {
        title: "Enhance operational efficiency and safety",
        icon: "pi pi-shield",
      },
      {
        title: "Enable real-time tracking and fleet management",
        icon: "pi pi-globe",
      },
    ],
    devices: [
      { title: "AGVs", icon: "pi pi-car" },
      { title: "AMRs", icon: "pi pi-microchip " },
      { title: "Proximity Sensors", icon: "pi pi-wifi" },
    ],
    applications: [
      { title: "Warehouse Automation Platforms", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 3: Smart Warehouse Automation",
    description:
      "Integrate IoT-enabled devices for automated sorting, picking, and packing in warehouses, powered by FlexNet's private 5G network.",
    benefits: [
      {
        title: "Increase operational speed and accuracy",
        icon: "pi pi-forward",
      },
      {
        title: "Reduce manual errors and labor costs",
        icon: "pi pi-user-minus",
      },
      {
        title: "Enhance scalability and flexibility in warehouse operations",
        icon: "pi pi-arrows-h",
      },
    ],
    devices: [
      { title: "IoT Devices", icon: "pi pi-cloud" },
      { title: "Automated Sorting Systems", icon: "pi pi-box" },
      { title: "Edge Computing Servers", icon: "pi pi-server" },
    ],
    applications: [
      { title: "Smart Warehouse Management Systems", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 4: Predictive Maintenance for Logistics Equipment",
    description:
      "Leverage IoT sensors to monitor logistics equipment, predicting maintenance needs to prevent unexpected failures and downtime.",
    benefits: [
      {
        title: "Increase equipment lifespan and reliability",
        icon: "pi pi-cog",
      },
      {
        title: "Reduce repair costs and operational disruptions",
        icon: "pi pi-dollar",
      },
      {
        title: "Enhance overall supply chain efficiency",
        icon: "pi pi-chart-line",
      },
    ],
    devices: [
      { title: "Vibration Sensors", icon: "pi pi-sliders-h" },
      { title: "Temperature Sensors", icon: "pi pi-sun" },
      { title: "IoT Gateways", icon: "pi pi-cloud" },
    ],
    applications: [
      { title: "Predictive Maintenance Solutions", icon: "pi pi-desktop" },
    ],
  },
  {
    title: " 5: Enhanced Video Surveillance and Monitoring",
    description:
      "Deploy 5G-enabled high-definition cameras throughout logistics facilities to monitor operations, ensure security, and analyze processes in real-time.",
    benefits: [
      {
        title:
          "Provides comprehensive coverage of warehouse and logistics areas",
        icon: "pi pi-video",
      },
      {
        title: "Enables real-time monitoring and quick response to incidents",
        icon: "pi pi-bell",
      },
      {
        title: "Facilitates advanced analytics for process optimization",
        icon: "pi pi-chart-bar",
      },
    ],
    devices: [
      { title: "5G-enabled HD cameras", icon: "pi pi-camera" },
      { title: "Edge computing devices", icon: "pi pi-server" },
      {
        title: "AI-powered video analytics platforms",
        icon: "pi pi-microchip",
      },
    ],
    applications: [
      { title: "Security surveillance systems", icon: "pi pi-desktop" },
      { title: "Operational monitoring dashboards", icon: "pi pi-chart-pie" },
      {
        title: "Safety compliance verification tools",
        icon: "pi pi-check-circle",
      },
    ],
  },
];
